import { useRouter } from 'next/router';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  MutableRefObject,
} from 'react';
import { EstimateOptionalType, toArray } from '@/helpers/optionals';
import useOptionals from '@/hooks/useOptionals';
import { api, local } from '@/pages/api/api';
import { VehicleType, DepotType, OptionalType } from '@/types';
import { useSettings } from './settings';
import { Estimate, Estimates } from '@/types/coral-types';
import { NextPaymentMethod } from '@/types/reservation';
import { applyAdjustments } from '@/helpers/applyAdjustments';

export type ItineraryType = {
  pickup_store: string;
  return_store: string;
  pickup_date: string;
  return_date: string;
  pickup_time: string;
  return_time: string;
};

type RateType = {
  rate_id: string;
  vehicle_rate: string;
  vehicle_class: string;
};

interface ReservationContextInterface {
  itinerary?: ItineraryType;
  setItinerary: (itinerary: ItineraryType) => void;
  rate?: any;
  optionals?: EstimateOptionalType[];
  setOptionals: (optionals: EstimateOptionalType[]) => void;
  renter?: any;
  estimate?: Estimate;
  estimates: Estimates;
  setEstimates: React.Dispatch<React.SetStateAction<Estimates>>;
  depot?: DepotType;
  returnDepot?: DepotType;
  vehicle?: VehicleType;
  setVehicle: (vehicle: VehicleType) => void;
  isPrePaid: boolean;
  paymentMethod: NextPaymentMethod;
  reservation: any;
  setReservation: any;
  setPaymentMethod: (method: NextPaymentMethod) => void;
  user: any;
  setUser: any;
  reset: () => void;
  cpdid: string;
  allOptionals: OptionalType[];
  setAllOptionals: (allOptionals: OptionalType[]) => void;
  loadReservationData: (data: {
    estimate?: Estimate;
    depot?: DepotType;
    returnDepot?: DepotType;
    vehicle?: VehicleType;
    paymentMethod?: NextPaymentMethod;
    optionals?: EstimateOptionalType[];
    itinerary?: ItineraryType;
    reservation?: any;
    user?: any;
    allOptionals?: OptionalType[];
  }) => void;
  revalidateEstimate: () => Promise<void>;
  processId: number | null;
  setProcessId: (id: number | null) => void;
  isEstimateLoading: boolean;
  userSelectedBasicProtection: boolean;
  setUserSelectedBasicProtection: React.Dispatch<React.SetStateAction<boolean>>;
  hashCode?: string;
  setHashCode: (code: string) => void;
  ignoreNextUpdateRef: MutableRefObject<boolean>;
}

export const CDPID_DATA = {
  pospaid: "WEBXML",
  creditCard: "FOCPRE",
  pix: "FOCPIX"
}

const Context = createContext<ReservationContextInterface>(
  {} as ReservationContextInterface,
);

export function ReservationProvider({ children }: PropsWithChildren) {
  const router = useRouter();
  const { query, isReady } = router;
  const { pixDiscount, creditCardDiscount, isReady: isSettingsReady, pagamentoPadrao, defaultSelectedProtection, basicProtection, specialProtection } = useSettings();
  const [itineraryReservation, setItinerary] = useState<ItineraryType | undefined>();
  const [processId, setProcessId] = useState<number | null>(null);
  const [hashCode, setHashCode] = useState<string | undefined>();

  const [pendingEstimateRequests, setPendingEstimateRequests] = useState(0);
  const isEstimateLoading = useMemo(() => pendingEstimateRequests > 0, [pendingEstimateRequests]);
  const latestEstimateRequestIdRef = useRef(0);

  const ignoreNextUpdateRef = useRef(false);

  const [userSelectedBasicProtection, setUserSelectedBasicProtection] = useState<boolean>(false);

  const fromQuery = useCallback(
    (fields: string[]) => {
      return fields.reduce((obj, key) => ({ ...obj, [key]: query[key] }), {});
    },
    [query],
  );

  const itinerary = useMemo<ItineraryType | undefined>(() => {
    if (itineraryReservation) {
      return itineraryReservation
    }

    if (query) {
      return fromQuery([
        'pickup_store',
        'return_store',
        'pickup_date',
        'return_date',
        'pickup_time',
        'return_time',
      ]) as ItineraryType;
    }
  }, [query, itineraryReservation]);

  const rate = useMemo<RateType | undefined>(() => {
    if (query) {
      return fromQuery(['rate_id', 'vehicle_rate', 'vehicle_class']) as RateType;
    }
  }, [query]);

  const cpdid = useMemo<string>(() => {
    if (query.cdp_id) {
      return query.cdp_id as string;
    }
    return 'WEBXML';
  }, [query]);

  const { opts } = useOptionals({ enabled: !!query.extras });

  const [renter, setRenter] = useState();
  const [optionals, setOptionals] = useState<EstimateOptionalType[]>();
  const [estimate, setEstimate] = useState<Estimate | undefined>();
  const [estimates, setEstimates] = useState<Estimates>({
    pix: {} as Estimate,
    cartao: {} as Estimate,
    pospago: {} as Estimate,
  });
  const [depot, setDepot] = useState<DepotType | undefined>();
  const [returnDepot, setReturnDepot] = useState<DepotType | undefined>();
  const [vehicle, setVehicle] = useState<VehicleType | undefined>();
  const [paymentMethod, setPaymentMethod] = useState<NextPaymentMethod>('creditCard');
  const [reservation, setReservation] = useState({});
  const [user, setUser] = useState({});
  const [allOptionals, setAllOptionals] = useState<OptionalType[]>([]);

  const isPrePaid = useMemo(
    () => paymentMethod === 'pix' || paymentMethod === 'creditCard',
    [paymentMethod],
  );

  const getEstimates = useCallback(async () => {
    if (ignoreNextUpdateRef.current) {
      console.log('skipping update');
      ignoreNextUpdateRef.current = false;
      return;
    }

    const data = {
      pickup_store: query.pickup_store,
      return_store: query.return_store,
      pickup_datetime: `${query.pickup_date}T${query.pickup_time}:00`,
      return_datetime: `${query.return_date}T${query.return_time}:00`,
      class_code: query.vehicle_class,
      promo_code: query.coupon,
      options: [] as EstimateOptionalType[],
      rate_ids: {
        pospago: query.rate_pospago,
        pix: query.rate_pix,
        cartao: query.rate_cartao,
      },
      discounts: {
        pospago: "0",
        pix: String(pixDiscount),
        cartao: String(creditCardDiscount),
      },
    };

    const extrasFromQuery = query.extras ? toArray(Array.isArray(query.extras) ? query.extras : [query.extras]) : null;

    if (extrasFromQuery && extrasFromQuery.length > 0) {
      data.options = extrasFromQuery;
    } else if (isSettingsReady && extrasFromQuery === null && !userSelectedBasicProtection) {
      if (defaultSelectedProtection?.coral_code) {
        data.options = [{ code: defaultSelectedProtection.coral_code, qtty: 1 }];
      } else if (basicProtection?.coral_code) {
        data.options = [{ code: basicProtection.coral_code, qtty: 1 }];
      }
    }


    if (query.vehicle_class && isSettingsReady) {
      setPendingEstimateRequests(prev => prev + 1);
      latestEstimateRequestIdRef.current += 1;
      const currentRequestId = latestEstimateRequestIdRef.current;

      try {
        const response = await local.post('/api/estimates', data);
        if (currentRequestId === latestEstimateRequestIdRef.current) {
          let fetchedEstimates: Estimates = {
            pix: response.data.estimates.pix,
            cartao: response.data.estimates.cartao,
            pospago: response.data.estimates.pospago,
          };

          const cashbackRescueValue = typeof query.cashback === 'string' ? Math.abs(parseFloat(query.cashback)) : 0;

          fetchedEstimates.pospago = applyAdjustments(fetchedEstimates.pospago, cashbackRescueValue);
          fetchedEstimates.pix = applyAdjustments(fetchedEstimates.pix, cashbackRescueValue);
          fetchedEstimates.cartao = applyAdjustments(fetchedEstimates.cartao, cashbackRescueValue);

          setEstimates(fetchedEstimates);
        }
      } catch (error) {
        console.error('Erro ao obter estimativas:', error);
        if (currentRequestId === latestEstimateRequestIdRef.current) {
        }
      } finally {
        setPendingEstimateRequests(prev => Math.max(0, prev - 1));
      }
    }
  }, [
    query,
    query.pickup_store,
    query.return_store,
    query.pickup_date,
    query.pickup_time,
    query.return_date,
    query.return_time,
    query.vehicle_class,
    query.coupon,
    query.rate_pospago,
    query.rate_pix,
    query.rate_cartao,
    query.extras,
    query.cashback,
    pixDiscount,
    creditCardDiscount,
    isSettingsReady,
    specialProtection,
    defaultSelectedProtection,
    basicProtection,
    userSelectedBasicProtection
  ]);

  useEffect(() => {
    if (isReady) {
      getEstimates();
    }
  }, [isReady, getEstimates]);

  useEffect(() => {
    if (estimates && paymentMethod) {
      const selectedEstimate =
        paymentMethod === 'pix'
          ? estimates.pix
          : paymentMethod === 'creditCard'
            ? estimates.cartao
            : estimates.pospago;
      setEstimate(selectedEstimate);
    }
  }, [estimates, paymentMethod]);

  useEffect(() => {
    if (query.extras && opts) {
      const aux = (fromQuery(['extras']) as any).extras;
      const extras = toArray(Array.isArray(aux) ? aux : [aux]);

      extras.forEach(e => {
        e.optional = opts.find(o => o.coral_code === e.code);
      });
      setOptionals(extras);
    } else if (typeof optionals !== 'undefined') {
      setOptionals(undefined);
    }
  }, [query.extras, opts, fromQuery]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: allDepots } = await local.get('/api/depots');
        if (Array.isArray(allDepots)) {
          const foundDepot = allDepots.find(d => d.coral_code === query.pickup_store);
          if (foundDepot) {
            setDepot(foundDepot);
          } else {
            setDepot(undefined);
          }
        } else {
          setDepot(undefined);
        }
      } catch (error) {
        setDepot(undefined);
      }
    };
    if (query.pickup_store) fetchData();
  }, [query.pickup_store]);

  useEffect(() => {
    const fetchReturnDepot = async () => {
      try {
        if (query.return_store) {
          const { data: allDepots } = await local.get('/api/depots');
          if (Array.isArray(allDepots)) {
            const foundDepot = allDepots.find(d => d.coral_code === query.return_store);
            if (foundDepot) {
              setReturnDepot(foundDepot);
            } else {
              setReturnDepot(undefined);
            }
          } else {
            setReturnDepot(undefined);
          }
        }
      } catch (error) {
        setReturnDepot(undefined);
      }
    };
    if (query.pickup_store === query.return_store && depot) {
      setReturnDepot(depot);
    } else if (query.return_store) {
      fetchReturnDepot();
    } else {
      setReturnDepot(undefined);
    }
  }, [query.return_store, query.pickup_store, depot]);

  useEffect(() => {
    const fetchData = async () => {
      const { data: vehicles } = await api(
        `/vehicles?${new URLSearchParams({ group: query.vehicle_class as any })}`,
      );
      setVehicle(vehicles[0]);
    };
    if (query.vehicle_class) {
      fetchData();
    }
  }, [query.vehicle_class]);

  useEffect(() => {
    if (isSettingsReady) {
      if (pagamentoPadrao === 'cartao') {
        setPaymentMethod('creditCard');
      } else if (pagamentoPadrao === 'pix') {
        setPaymentMethod('pix');
      } else if (pagamentoPadrao === 'posPago') {
        setPaymentMethod('pospaid');
      } else {
        setPaymentMethod('creditCard');
      }
    }
  }, [isSettingsReady, pagamentoPadrao]);

  const loadReservationData = useCallback((data: {
    estimate?: Estimate;
    depot?: DepotType;
    returnDepot?: DepotType;
    vehicle?: VehicleType;
    paymentMethod?: NextPaymentMethod;
    optionals?: EstimateOptionalType[];
    itinerary?: ItineraryType;
    reservation?: any;
    user?: any;
    allOptionals?: OptionalType[];
  }) => {
    if (data.estimate) setEstimate(data.estimate);
    if (data.depot) setDepot(data.depot);
    if (data.returnDepot) setReturnDepot(data.returnDepot);
    if (data.vehicle) setVehicle(data.vehicle);
    if (data.paymentMethod) setPaymentMethod(data.paymentMethod);
    if (data.optionals) setOptionals(data.optionals);
    if (data.itinerary) setItinerary(data.itinerary);
    if (data.reservation) setReservation(data.reservation);
    if (data.user) setUser(data.user);
    if (data.allOptionals) setAllOptionals(data.allOptionals);
  }, [setEstimate, setDepot, setReturnDepot, setVehicle, setPaymentMethod, setOptionals, setItinerary, setReservation, setUser, setAllOptionals]);

  const reset = () => {
    setRenter(undefined);
    setOptionals(undefined);
    setEstimate(undefined);
    setEstimates({ pix: {} as Estimate, cartao: {} as Estimate, pospago: {} as Estimate });
    setDepot(undefined);
    setReturnDepot(undefined);
    setVehicle(undefined);
    setPaymentMethod('creditCard');
    setReservation({});
    setUser({});
    setProcessId(null);
    setPendingEstimateRequests(0);
    latestEstimateRequestIdRef.current = 0;
  };

  const revalidateEstimate = useCallback(async () => {
    await getEstimates();
  }, [getEstimates]);

  return (
    <Context.Provider
      value={{
        depot,
        returnDepot,
        vehicle,
        setVehicle,
        itinerary,
        rate,
        optionals,
        setOptionals,
        setItinerary,
        renter,
        estimate,
        estimates,
        setEstimates,
        isPrePaid,
        paymentMethod,
        setPaymentMethod,
        reservation,
        setReservation,
        user,
        setUser,
        reset,
        cpdid,
        allOptionals,
        setAllOptionals,
        loadReservationData,
        revalidateEstimate,
        processId,
        setProcessId,
        isEstimateLoading,
        userSelectedBasicProtection,
        setUserSelectedBasicProtection,
        hashCode,
        setHashCode,
        ignoreNextUpdateRef,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useReservation() {
  return useContext(Context);
}